import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, List, Grid, Button, ListItem, Typography, ListItemText } from '@material-ui/core';

import { useStyles, listTextStyles, customCardStyles } from './UserDetails.styled';
import {
  AssignNfcModal,
  UpdateUserModal,
  DeleteUserModal,
  ChangeUserStatusModal,
} from 'components/Modals';
import { usersActions } from 'state/modules/users';
import ItemStatus from 'components/ItemStatus';
import { withRouter } from 'react-router-dom';
import { statusReasons } from 'constants';
import { Card } from 'components/UI';
import Can from 'components/Can';
import Actions from '../Actions';
import moment from 'moment';
import i18n from 'i18n-js';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';

const UserDetails = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();
  const [open, setOpen] = useState(false);
  const { user } = useSelector((state) => state.users);
  const [openNfcModal, setOpenNfcModal] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);
  const [openUpdateUserModal, setOpenUpdateUserModal] = useState(false);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
  const bastion = useSelector((state) => state.organizations?.organization?.bastion?.id_slug) || '';

  useEffect(() => {
    if (id) {
      dispatch(usersActions.fetchUserById(id));
    }
  }, [id]); // eslint-disable-line

  /**
   * Parse user data
   * @param {object} user Object with user data
   */
  const getUserData = (user = {}) => {
    const userInfo = [
      { label: 'Full Name:', value: `${user?.first_name || ''} ${user?.last_name || ''}` || '-' },
      {
        label: 'Email:',
        value: user?.email || '-',
        confirmed: user?.email_confirmed_at || 'Unconfirmed',
      },
      { label: 'Phone:', value: user?.mobile_phone || '-' },
      { label: 'Role:', value: i18n.t(`roles.${user?.role}`) || '-', classes: 'capitalize' },
      {
        label: `${i18n.t('customers.internal_id')}:`,
        value: user?.internal_id || '-',
        tooltip: i18n.t('users.internal_id_tooltip'),
      },
    ];

    return userInfo;
  };

  /**
   * Parse user status data
   * @param {object} user Object with user status data
   */
  const getUserStatusData = (user = {}) => {
    const statusDescription = statusReasons?.find(
      (status) => status.value === user?.status_details?.[0]
    );
    const userInfo = [
      { label: `${i18n.t('users.reason')}:`, value: statusDescription?.label || '-' },
      { label: 'Description:', value: user?.status_notes || '-' },
    ];

    return userInfo;
  };

  /**
   * Open change user status modal
   */
  const handleChangeUserStatus = () => {
    setOpen((prevState) => !prevState);
  };

  /**
   * Open change user status modal
   */
  const handleUpdateUserModal = () => {
    setOpenUpdateUserModal((prevState) => !prevState);
  };

  /**
   * Open change user status modal
   */
  const handleNfcModal = () => {
    setOpenNfcModal((prevState) => !prevState);
  };

  /**
   * handle Delete user modal
   */
  const handleDeleteUserModal = () => {
    setOpenDeleteUserModal((prevState) => !prevState);
  };

  /**
   * Send confirmation link
   */
  const handleConfirmationLink = () => {
    if (id && user) {
      dispatch(usersActions.fetchSendConfirmationLink(id, user.email));
    }
  };

  /**
   * Send password reset link
   */
  const handleSendPasswordResetLink = () => {
    if (id && user && bastion) {
      dispatch(usersActions.fetchSendPasswordReset(user.email, bastion));
    }
  };

  /**
   * Send mobile phone confirm SMS
   */
  const handleSendPhoneConfirmSMS = () => {
    if (id && user) {
      dispatch(usersActions.fetchSendPhoneConfirmationSMS(id, user.mobile_phone));
    }
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('users.change_status'),
      onClick: handleChangeUserStatus,
    },
    {
      title: i18n.t('users.confirmation_link'),
      onClick: handleConfirmationLink,
    },
    {
      title: i18n.t('users.reset_pwd_link'),
      onClick: handleSendPasswordResetLink,
      'data-cy': 'pwd-reset-link-btn',
    },
    {
      title: i18n.t('users.confirm_sms'),
      onClick: handleSendPhoneConfirmSMS,
      enabled: !!user?.mobile_phone,
    },
    {
      title: i18n.t('users.assign_nfc'),
      feature: { name: 'access_device', is: 'enabled' },
      onClick: handleNfcModal,
    },
    {
      title: i18n.t('users.delete'),
      onClick: handleDeleteUserModal,
    },
  ];

  return (
    <Card
      menuItems={items}
      menuPermissions={{
        action: 'update',
        subject: 'user',
        type: 'can',
      }}
      title={i18n.t('users.user_information')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
      data-cy="users-widget-card"
    >
      <Box className={classes.cardContentPadding}>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <Grid item xs={8}>
            <Typography variant="h3">
              <Box className={classes.name}>ID# {id}</Box>
            </Typography>
          </Grid>
          {user?.status && (
            <Grid item xs={4} className={`${classes.textRight}`} data-cy="employee-status">
              <ItemStatus i18nKey="userStatus" status={user?.status} />
            </Grid>
          )}
          <Can I="update" on="user">
            <Grid item xs={12} className={`${classes.textRight}`}>
              <Button
                className={classes.updateButton}
                onClick={handleUpdateUserModal}
                color="primary"
              >
                {i18n.t('users.update_info')}
              </Button>
            </Grid>
          </Can>
        </Grid>

        {user?.status && user?.status !== 'ACTIVE' && (
          <List className={classes.userList} data-cy="user-data">
            {getUserStatusData(user).map((val, index) => (
              <ListItem key={index} className={classes.userListItem}>
                <ListItemText
                  classes={listClasses}
                  primary={val.label}
                  secondary={
                    <Box
                      display="flex"
                      component="span"
                      flexDirection="column"
                      className={classes.textRight}
                    >
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                        className={classes?.[val.classes]}
                      >
                        {val.value}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        <Actions id={id} listStyles={listClasses} />

        <List className={classes.userList} data-cy="user-data">
          {getUserData(user).map((val, index) => (
            <ListItem key={index} className={classes.userListItem}>
              <ListItemText
                classes={listClasses}
                primary={
                  <Box display="flex" alignItems="center">
                    {val.label}
                    {val?.tooltip ? (
                      <Tooltip title={val.tooltip}>
                        <InfoIcon className={classes.infoIcon} />
                      </Tooltip>
                    ) : null}
                  </Box>
                }
                secondary={
                  <Box
                    display="flex"
                    component="span"
                    flexDirection="column"
                    className={classes.textRight}
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      color="textSecondary"
                      className={classes?.[val.classes]}
                    >
                      {val.value}
                    </Typography>
                    {val.hasOwnProperty('confirmed') && val.confirmed !== 'Unconfirmed' && (
                      <Typography variant="body2" component="span" className={classes.confirmed}>
                        {i18n.t('users.confirmed')}:{' '}
                        {moment(val.confirmed).format(`${localeFormat} hh:mm a`)}
                      </Typography>
                    )}
                    {val.hasOwnProperty('confirmed') && val.confirmed === 'Unconfirmed' && (
                      <Typography variant="body2" component="span" className={classes.unconfirmed}>
                        {val.confirmed}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
      {open && (
        <ChangeUserStatusModal
          id={id}
          open={open}
          status={user?.status}
          handleClick={handleChangeUserStatus}
          userType="EMPLOYEE"
        />
      )}
      {openUpdateUserModal && (
        <UpdateUserModal
          id={id}
          user={user}
          open={openUpdateUserModal}
          handleClick={handleUpdateUserModal}
        />
      )}
      {openDeleteUserModal && (
        <DeleteUserModal
          id={id}
          user={user}
          type="user"
          open={openDeleteUserModal}
          handleClick={handleDeleteUserModal}
        />
      )}
      {openNfcModal && (
        <AssignNfcModal id={id} user={user} open={openNfcModal} handleClick={handleNfcModal} />
      )}
    </Card>
  );
};

export default withRouter(UserDetails);
